// assets/email-signup.js
/**
 * Email Signup Block Component
 * Handles AJAX form submission and manually displays success message.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    this.lastButtonWidth = 0;
    this.observer = undefined;
    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');

    if (this.form && !this.form.hasAttribute('data-ajax-bound')) {
      this.form.setAttribute('data-ajax-bound', 'true');
      this.form.addEventListener('submit', this.onSubmit);
    }

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();
      this.observer = new ResizeObserver(() => this.adjustInputPadding());
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => this.adjustInputPadding());
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;
    const buttonWidth = this.button.offsetWidth;
    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      this.inputGroup.style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }

  onSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.form || !this.submitButton) return;

    const originalBtnContent = this.submitButton.innerHTML;
    this.submitButton.innerHTML = '...';
    this.submitButton.disabled = true;

    const formData = new FormData(this.form);

    fetch(this.form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      // Check for Captcha challenge
      if (response.url && response.url.includes('/challenge')) {
        window.location.href = response.url;
        return;
      }

      // If submission was successful (Status 200 OK)
      if (response.ok) {
        this.showSuccessMessage();
      } else {
        // Handle errors (like invalid email) by parsing response
        return response.text().then(html => this.handleError(html));
      }
    })
    .catch(error => {
      console.error('Error submitting form:', error);
      this.submitButton.innerHTML = originalBtnContent;
      this.submitButton.disabled = false;
    });
  }

  showSuccessMessage() {
    // 1. Find the template we added in Liquid
    const successTemplate = this.querySelector('.success-message-template');
    
    if (successTemplate) {
      // 2. Clone the content inside the template
      const successContent = successTemplate.content.cloneNode(true);
      
      // 3. Clear the form content completely
      this.innerHTML = '';
      
      // 4. Inject the success message
      this.appendChild(successContent);
      
      console.log('✅ Success message displayed.');
    } else {
      console.error('Template .success-message-template not found in Liquid block.');
      // Fallback text if template is missing
      this.innerHTML = '<div style="color:green; padding:10px;">Subscribed successfully!</div>';
    }
  }

  handleError(html) {
    // If there was an error (e.g., "Email already subscribed"), replace the block 
    // with the HTML from Shopify which contains the error message.
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newBlock = doc.getElementById(this.id);
    
    if (newBlock) {
      this.innerHTML = newBlock.innerHTML;
      this.connectedCallback();
    } else {
      this.submitButton.innerHTML = 'Try Again';
      this.submitButton.disabled = false;
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}